import { cn } from '@/shared/utils';
import { CustomTooltip } from '@/view/shared/Tooltip';
import type { InputProps } from '@/view/shared/form/Input.props';
import { Description, Input as HeroInput, Label, TextField } from '@heroui/react';
import { Eye, EyeOff, Info } from 'lucide-react';
import { useState } from 'react';

export function Input({
  label,
  errorMessage,
  description,
  placeholder,
  classNames,
  type,
  name,
  value,
  isValid,
  leftIcon: LeftIcon,
  tooltip,
  labelSuffix,
  onChange,
}: Readonly<InputProps>) {
  const [passwordVisible, setPasswordVisible] = useState(false);

  const hasError = !isValid && Boolean(value);
  const inputClassName = cn(
    'form-input w-full h-8 sm:h-9 border border-input rounded-lg px-3 bg-card text-foreground',
    'focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary transition-all',
    'placeholder:text-muted-foreground text-xs sm:text-sm font-normal leading-normal',
    LeftIcon && 'pl-8',
    type === 'password' && 'pr-10',
    hasError && 'border-destructive focus:ring-destructive/30 focus:border-destructive',
    classNames?.input,
  );

  return (
    <TextField
      fullWidth
      isInvalid={hasError}
      validationBehavior="aria"
      className={classNames?.container ?? ''}
    >
      {label && (
        <Label className={classNames?.label ?? ''}>
          {label}
          {labelSuffix && (
            <span className="text-xs font-normal text-muted-foreground ml-1">{labelSuffix}</span>
          )}
        </Label>
      )}
      <div className="flex items-center gap-1.5">
        <div className="relative w-full">
          {LeftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
              <LeftIcon className="w-3.5 h-3.5" />
            </div>
          )}
          <HeroInput
            id={name}
            name={name}
            type={type === 'password' && passwordVisible ? 'text' : type}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder ?? ''}
            className={inputClassName}
          />
          {type === 'password' && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <button
                type="button"
                className="cursor-pointer text-muted hover:text-jade transition-colors"
                onClick={() => setPasswordVisible(!passwordVisible)}
              >
                {passwordVisible ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              </button>
            </div>
          )}
        </div>
        {tooltip && (
          <CustomTooltip tip={tooltip} placement="top" delay={200}>
            <button
              type="button"
              className="shrink-0 cursor-pointer flex items-center justify-center rounded-md w-6 h-6 text-muted-foreground/60 hover:text-primary hover:bg-primary/10 transition-colors"
            >
              <Info className="w-3.5 h-3.5" />
            </button>
          </CustomTooltip>
        )}
      </div>
      {description && !hasError && (
        <Description className={classNames?.description ?? ''}>{description}</Description>
      )}
      <output
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out',
          hasError && errorMessage ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <p className={cn('overflow-hidden text-body-xs text-destructive', classNames?.error)}>
          {errorMessage}
        </p>
      </output>
    </TextField>
  );
}
